import { Button, Collapse } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import LockIcon2 from "../../../../assets/icons/LockIcon2";
import MinusIcon from "../../../../assets/icons/MinusIcon";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import RightLongArrow from "../../../../assets/icons/RightLongArrow";
import TopCard from "../../../../components/common/TopCard";
import Loader from "../../../../components/ui/Loader";
import colors from "../../../../constants/colors";
import {
  useGetAttendanceSettingDataQuery,
  useGetUnpaidLeavePolicyQuery,
} from "../../../../features/attendanceSetting/attendanceSettingApi";
import {
  useGetPayrollSettingQuery,
  useUpdatePayComponentMutation,
} from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { PayrollSettingResponseType } from "../../../../types/payroll";
import { generateUUID } from "../../../../utils/number";
import { underscoreToCapitalizeString } from "../../../../utils/utils";
import AdditionsModal from "./AdditionsModal";
import DeductionsModal from "./DeductionsModal";
import HolidayAllowanceSettingModal from "./HolidayAllowanceSettingModal";
import OvertimeModal from "./OvertimeModal";
import { payrollSettingsTabList } from "./payrollSetting";

export type PayComponentModalType =
  | "ADDITION_ADD"
  | "ADDITION_EDIT"
  | "DEDUCTION_ADD"
  | "DEDUCTION_EDIT"
  | "OVERTIME_MODAL"
  | "HOLIDAY_MODAL";

type PayAdditionData = {
  title: string | number | null;
  type?: "ADD" | "EDIT" | "DELETE";
} & PayrollSettingResponseType["data"]["payroll_setting"]["pay_components"]["addition"][0];
type PayDeductionData = {
  title: string | number | null;
  type?: "ADD" | "EDIT" | "DELETE";
} & PayrollSettingResponseType["data"]["payroll_setting"]["pay_components"]["deduction"][0];

export default function PayComponents() {
  const { open, handleClose, handleOpen } = useModal<
    any,
    PayComponentModalType
  >();
  const [activeAddition, setActiveAddition] = useState<string>("");
  const [activeDeduction, setActiveDeduction] = useState<string>("");
  const [additionActiveKeys, setAdditionActiveKeys] = useState<string[]>();
  const [deductionsActiveKeys, setDeductionsActiveKeys] = useState<string[]>(
    []
  );
  const { data: payrollSettingData, isLoading: isGetLoading } =
    useGetPayrollSettingQuery();
  const payComponents =
    payrollSettingData?.data?.payroll_setting?.pay_components;
  const [additionDataList, setAdditionDataList] = useState<PayAdditionData[]>(
    []
  );
  const [deductionDataList, setDeductionDataList] = useState<
    PayDeductionData[]
  >([]);

  const {
    data: unpaidLeaveData,
    isError: isUnpaidLeaveError,
    error: unpaidLeaveError,
  } = useGetUnpaidLeavePolicyQuery();
  const {
    isError: isAttendanceError,
    error: attendanceError,
    data: attendanceData,
  } = useGetAttendanceSettingDataQuery({});

  const [updatePayComponent, { isLoading, isError, isSuccess, error }] =
    useUpdatePayComponentMutation();
  useError(isError, error, "Pay component update fail");
  useError(isUnpaidLeaveError, unpaidLeaveError, "Unpaid leave data fail");
  useError(isAttendanceError, attendanceError, "Unpaid leave data fail");

  const handleDataList = (data: any, type: "ADD" | "EDIT" | "DELETE") => {
    if (type === "ADD") {
      setAdditionDataList((prev: any) => [
        ...prev,
        { ...data, id: generateUUID(), type: "ADD" },
      ]);
      return;
    }

    if (type === "EDIT") {
      setAdditionDataList((prev: any) =>
        prev?.map((item: any) => {
          if (item?.id === data?.id) {
            return { ...data, type: "EDIT" };
          }

          return item;
        })
      );
      return;
    }

    if (type === "DELETE") {
      setAdditionDataList((prev: any) =>
        prev?.map((item: any) => {
          if (item?.id === data?.id) {
            return { ...data, type: "DELETE" };
          }

          return item;
        })
      );
      return;
    }
  };

  const handleDeductionDataList = (
    data: any,
    type: "ADD" | "EDIT" | "DELETE"
  ) => {
    if (type === "ADD") {
      setDeductionDataList((prev: any) => [
        ...prev,
        { ...data, id: generateUUID(), type: "ADD" },
      ]);
      return;
    }

    if (type === "EDIT") {
      setDeductionDataList((prev: any) =>
        prev?.map((item: any) => {
          if (item?.id === data?.id) {
            return { ...data, type: "EDIT" };
          }

          return item;
        })
      );
      return;
    }

    if (type === "DELETE") {
      setDeductionDataList((prev: any) =>
        prev?.map((item: any) => {
          if (item?.id === data?.id) {
            return { ...data, type: "DELETE" };
          }

          return item;
        })
      );
      return;
    }
  };

  const deductionsDefaultData: {
    [key: string]: { text: string; link?: string; hideLock?: boolean };
  } = {
    tax: {
      text: "System will be considering Bangladesh government rule on calculating the tax for employees. To learn more about the rules please click the link below.",
      link: "https://nbr.gov.bd/regulations/rules/income-tax-rules/eng",
    },
    leave_adjustment: {
      text: "This is an automatically calculated leave adjustment amount based on organization preferences. To know more please click the below button.",
      link: "/admin/settings/office-settings/leave",
      hideLock: !!unpaidLeaveData?.data?.is_unpaid_leave_policy_enable,
    },
    attendance_adjustment: {
      text: "This is an automatically calculated attendance adjustment count based on organization preferences. To know more please click the below button",
      link: "/admin/settings/office-settings/attendance",
      hideLock:
        attendanceData?.data?.office_settings_attendance
          ?.is_grace_period_policy_enable &&
        attendanceData?.data?.office_settings_attendance
          ?.is_late_checkin_early_checkout_policy_enable
          ? true
          : false,
    },
  };

  const handleSubmit = () => {
    const data = {
      addition: {
        add: additionDataList
          ?.filter(
            (item: any) =>
              item?.type === "ADD" ||
              (item?.type === "EDIT" && typeof item?.id !== "number")
          )
          ?.map((item: any) => ({ ...item, id: undefined, type: undefined })),
        update: additionDataList
          ?.filter(
            (item: any) => item?.type === "EDIT" && typeof item?.id === "number"
          )
          ?.map((item: any) => ({ ...item, type: undefined })),
      },
      deduction: {
        add: deductionDataList
          ?.filter(
            (item: any) =>
              item?.type === "ADD" ||
              (item?.type === "EDIT" && typeof item?.id !== "number")
          )
          ?.map((item: any) => ({ ...item, id: undefined, type: undefined })),
        update: deductionDataList
          ?.filter(
            (item: any) => item?.type === "EDIT" && typeof item?.id === "number"
          )
          ?.map((item: any) => ({ ...item, type: undefined })),
      },
      component_delete_data: {
        addition: additionDataList
          ?.filter(
            (item: any) =>
              item?.type === "DELETE" && typeof item?.id === "number"
          )
          ?.map((item: any) => item?.id),
        deduction: deductionDataList
          ?.filter(
            (item: any) =>
              item?.type === "DELETE" && typeof item?.id === "number"
          )
          ?.map((item: any) => item?.id),
      },
    };

    updatePayComponent({
      id: payrollSettingData?.data?.payroll_setting?.id,
      data,
    });
  };

  const additionsItems = additionDataList
    ?.filter((item) => item?.type !== "DELETE")
    ?.map((item) => {
      const isActive = additionActiveKeys?.includes(item?.id?.toString());

      return {
        key: item?.id?.toString(),
        label: (
          <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              {item?.is_default === 1 && <LockIcon2 />}

              <h6 className="text-black text-base font-medium">
                {item?.title || item?.value || item?.key
                  ? item?.title ||
                    item?.value ||
                    underscoreToCapitalizeString(String(item?.key))
                  : null}
              </h6>
            </div>
            {isActive ? <MinusIcon /> : <PlusIcon />}
          </div>
        ),
        children:
          item?.is_default === 1 ? (
            <p className="text-display-body text-sm">
              {item?.title || additionsDefaultData?.[item?.key]?.text || ""}
            </p>
          ) : null,
      };
    });

  const deductionsItems = deductionDataList
    ?.filter((item) => item?.type !== "DELETE")
    ?.map((item) => {
      const isActive = deductionsActiveKeys?.includes(item?.id?.toString());
      const link = deductionsDefaultData?.[item?.key]?.link;

      return {
        key: item?.id?.toString(),
        label: (
          <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              {item?.is_default === 1 &&
                !deductionsDefaultData?.[item?.key].hideLock && <LockIcon2 />}

              <h6 className="text-black text-base font-medium">
                {item?.title || item?.value || item?.key
                  ? item?.title ||
                    item?.value ||
                    underscoreToCapitalizeString(String(item?.key))
                  : null}
              </h6>
            </div>
            {isActive ? <MinusIcon /> : <PlusIcon />}
          </div>
        ),
        children:
          item?.is_default === 1 ? (
            <div className="text-display-body text-sm">
              {item?.title || deductionsDefaultData?.[item?.key]?.text || ""}
              {link ? (
                <>
                  <br />
                  <div className="!text-accent text-sm font-semibold inline-flex items-center gap-2 mt-2 ">
                    {link?.startsWith("http") || link?.startsWith("https") ? (
                      <a
                        className="!text-accent"
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Know More
                      </a>
                    ) : (
                      <Link className="!text-accent" to={link}>
                        Know More
                      </Link>
                    )}

                    <RightLongArrow />
                  </div>
                </>
              ) : null}
            </div>
          ) : null,
      };
    });

  const handleAdditionsCollapseChange = (keys: string | string[]) => {
    // const additionData =
    //   payrollSettingData?.data?.payroll_setting?.pay_components?.addition;

    const findData = additionDataList?.find(
      (item) =>
        !item?.is_default && item?.id?.toString() === keys?.[0]?.toString()
    );

    if (findData) {
      handleOpen({ type: "ADDITION_EDIT", data: findData });
      setActiveAddition("");
    }

    const findOverTime = additionDataList?.find(
      (item) =>
        item?.key === "overtime" &&
        item?.id?.toString() === keys?.[0]?.toString()
    );

    if (findOverTime) {
      handleOpen({ type: "OVERTIME_MODAL", data: findOverTime });
    }

    const findHoliday = additionDataList?.find(
      (item) =>
        item?.key === "holiday_allowance" &&
        item?.id?.toString() === keys?.[0]?.toString()
    );

    if (findHoliday) {
      handleOpen({ type: "HOLIDAY_MODAL", data: findHoliday });
    }

    setAdditionActiveKeys(keys as string[]);
  };

  const handleDeductionsCollapseChange = (keys: string | string[]) => {
    const findData = deductionDataList?.find(
      (item) =>
        !item?.is_default && item?.id?.toString() === keys?.[0]?.toString()
    );

    if (findData) {
      handleOpen({ type: "DEDUCTION_EDIT", data: findData });
      setActiveDeduction("");
    }
    setDeductionsActiveKeys(keys as string[]);
  };

  useEffect(() => {
    if (payComponents?.addition?.length) {
      setAdditionDataList(
        payComponents?.addition?.map((item) => ({
          ...item,
          title: item?.value,
        }))
      );

      // setAdditionActiveKeys([payComponents?.addition?.[0]?.id?.toString()]);
    }
  }, [payComponents?.addition]);

  useEffect(() => {
    if (payComponents?.deduction?.length) {
      setDeductionDataList(
        payComponents?.deduction?.map((item) => ({
          ...item,
          title: item?.value,
        }))
      );

      setDeductionsActiveKeys([payComponents?.deduction?.[0]?.id?.toString()]);
    }
  }, [payComponents?.deduction]);

  // useEffect(() => {
  //   if (`${payComponents?.addition?.[0]?.id}`)
  //     setActiveAddition(`${payComponents?.addition?.[0]?.id}`);
  // }, [payComponents?.addition]);

  useEffect(() => {
    if (`${payComponents?.deduction?.[0]?.id}`)
      setActiveDeduction(`${payComponents?.deduction?.[0]?.id}`);
  }, [payComponents?.deduction]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Pay components successfully added!");
    }
  }, [isSuccess]);

  return (
    <SidebarLayout title="Pay Components" tabList={payrollSettingsTabList}>
      {(open.type === "ADDITION_ADD" || open?.type === "ADDITION_EDIT") && (
        <AdditionsModal
          open={open.open}
          type={open?.type}
          data={open?.data}
          handleClose={() => {
            handleClose();
            if (open?.type === "ADDITION_EDIT") {
              setAdditionActiveKeys([""]);
              setActiveAddition("");
            }
          }}
          handleDataList={handleDataList}
        />
      )}
      {(open.type === "DEDUCTION_ADD" || open?.type === "DEDUCTION_EDIT") && (
        <DeductionsModal
          open={open.open}
          type={open?.type}
          data={open?.data}
          handleClose={() => {
            handleClose();
            if (open?.type === "DEDUCTION_EDIT") {
              setDeductionsActiveKeys([""]);
              setActiveDeduction("");
            }
          }}
          handleDataList={handleDeductionDataList}
        />
      )}

      {open.type === "OVERTIME_MODAL" && (
        <OvertimeModal
          open={open?.open}
          data={open?.data}
          handleClose={() => {
            handleClose();
            if (open?.type === "OVERTIME_MODAL") {
              setAdditionActiveKeys([""]);
              setActiveAddition("");
            }
          }}
        />
      )}

      {open.type === "HOLIDAY_MODAL" && (
        <HolidayAllowanceSettingModal
          open={open?.open}
          data={open?.data}
          handleClose={() => {
            handleClose();
            if (open?.type === "HOLIDAY_MODAL") {
              setAdditionActiveKeys([""]);
              setActiveAddition("");
            }
          }}
        />
      )}

      <TopCard
        leftChildren={
          <span className="w-full text-primary font-normal text-base">
            Please click the <span className="font-semibold">'Update'</span>{" "}
            button to apply any changes
          </span>
        }
      >
        {/* <Button className="normal-dark-button modal-button" size="middle">
          Cancel
        </Button> */}
        <Button
          className="modal-button"
          type="primary"
          size="middle"
          onClick={handleSubmit}
          loading={isLoading}
        >
          Update
        </Button>
      </TopCard>

      <div className="grid grid-cols-2 gap-6 items-start pay_components">
        <div className="bg-white rounded-md border border-extra-light p-4">
          <div className="flex justify-between items-center">
            <h5 className="text-header text-xl font-semibold leading-loose">
              Additions
            </h5>

            <Button
              className="outline-button-primary"
              onClick={() => {
                handleOpen({ type: "ADDITION_ADD" });
              }}
              icon={<AddIcon color={colors.darkHighlight} />}
            >
              Add New
            </Button>
          </div>
          {isGetLoading && <Loader />}
          <Collapse
            items={additionsItems}
            activeKey={[activeAddition]}
            onChange={(val) => {
              handleAdditionsCollapseChange(val);
              setActiveAddition(typeof val === "string" ? val : val[0]);
            }}
            className="mt-4 border-none"
            accordion
          />
        </div>
        <div className="bg-white p-4">
          <div className="flex justify-between items-center">
            <div className="text-[20px] font-semibold text-[#2D3643]">
              Deductions
            </div>

            <Button
              className="outline-button-primary"
              size="small"
              onClick={() => {
                handleOpen({ type: "DEDUCTION_ADD" });
              }}
              icon={<AddIcon color={colors.darkHighlight} />}
            >
              Add New
            </Button>
          </div>

          {isGetLoading && <Loader />}
          <Collapse
            items={deductionsItems}
            activeKey={[activeDeduction]}
            onChange={(val) => {
              handleDeductionsCollapseChange(val);
              setActiveDeduction(typeof val === "string" ? val : val[0]);
            }}
            className="mt-4 border-none"
            accordion
          />
        </div>
      </div>
    </SidebarLayout>
  );
}

const additionsDefaultData: { [key: string]: { text: string; link?: string } } =
  {
    overtime: {
      text: "This component is not calculated automatically. Thus you need to provide the overtime amount manually after each payroll is generated for the employees who are applicable for overtime benefit.",
    },
  };
